import { useEffect, useState } from 'react';
import { SITE_ID } from '@/config/site';

/**
 * Home-screen install state, abstracted across the two very different
 * platform paths:
 *
 *   - Chrome/Edge/Android/desktop fire a `beforeinstallprompt` event we can
 *     capture and replay on a user gesture (`promptInstall`).
 *   - iOS Safari fires nothing and exposes no install API — the only path is
 *     the user manually choosing Share → "Add to Home Screen", so we surface
 *     instructions instead of a button (`isIOS`).
 *
 * Detecting that the app is ALREADY installed is the subtle part. Inside the
 * installed window `isStandalone` is true, but a normal browser tab on the same
 * origin looks exactly like a first-ever visit — which is why installed users
 * kept getting nagged. Three signals feed `isInstalled`:
 *
 *   1. `isStandalone` — we're running in the installed window right now.
 *   2. `navigator.getInstalledRelatedApps()` — Chromium only, and authoritative
 *      in both directions (it also reports an uninstall). Requires the manifest
 *      to list itself under `related_applications`.
 *   3. A sticky localStorage flag written whenever 1 or 2 says yes, or when
 *      `appinstalled` fires. This is the only thing that carries the answer
 *      into a plain Safari tab, where iOS gives us nothing to query.
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

interface RelatedApplication {
  id?: string;
  platform: string;
  url?: string;
}

type InstallAwareNavigator = Navigator & {
  standalone?: boolean;
  getInstalledRelatedApps?: () => Promise<RelatedApplication[]>;
};

function installedKey(): string {
  return `rumio:pwa-installed:${SITE_ID}`;
}

function readInstalled(): boolean {
  try {
    return localStorage.getItem(installedKey()) === '1';
  } catch {
    return false;
  }
}

function writeInstalled(installed: boolean): void {
  try {
    if (installed) localStorage.setItem(installedKey(), '1');
    else localStorage.removeItem(installedKey());
  } catch {
    /* swallow quota / privacy-mode errors */
  }
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // `display-mode: standalone` covers Android/desktop installs;
  // `navigator.standalone` is the iOS-Safari-only equivalent.
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as InstallAwareNavigator).standalone === true
  );
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOSDevice =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ reports as a Mac; disambiguate via touch support.
    (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  // Add-to-Home-Screen on iOS only exists in Safari, not Chrome/Firefox/in-app
  // webviews (which render via WebKit but lack the Share → A2HS option).
  const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  return isIOSDevice && isSafari;
}

export interface InstallPromptState {
  /** A `beforeinstallprompt` event was captured — `promptInstall` will work. */
  canInstall: boolean;
  /** iOS Safari: no programmatic prompt; show manual Share-sheet instructions. */
  isIOS: boolean;
  /** Running inside the installed window right now. */
  isStandalone: boolean;
  /** Installed on this device — in the installed window or a plain browser tab. */
  isInstalled: boolean;
  /** Replay the captured prompt. Resolves to the user's choice, or null on iOS. */
  promptInstall: () => Promise<'accepted' | 'dismissed' | null>;
}

function relatedAppsSupported(): boolean {
  return typeof (navigator as InstallAwareNavigator).getInstalledRelatedApps === 'function';
}

export function useInstallPrompt(): InstallPromptState {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone] = useState<boolean>(detectStandalone);
  const [wasInstalled, setWasInstalled] = useState<boolean>(readInstalled);
  // `beforeinstallprompt` can land before the async related-apps answer does.
  // Hold every install affordance until we know, or an installed user sees the
  // banner flash on screen before it disappears.
  const [checking, setChecking] = useState<boolean>(relatedAppsSupported);
  const isIOS = detectIOS();

  const remember = (installed: boolean) => {
    writeInstalled(installed);
    setWasInstalled(installed);
  };

  useEffect(() => {
    // Launched from the home screen ⇒ installed. On iOS this is the one and
    // only moment we ever learn that, so persist it for later Safari visits.
    if (isStandalone) remember(true);
  }, [isStandalone]);

  useEffect(() => {
    const nav = navigator as InstallAwareNavigator;
    if (!nav.getInstalledRelatedApps) return;

    let cancelled = false;
    nav
      .getInstalledRelatedApps()
      .then((apps) => {
        if (cancelled) return;
        const installed = apps.some((app) => app.platform === 'webapp');
        // An empty list here means Chromium knows we're not installed, so it
        // clears a stale flag left behind by an uninstall. Skip that when we're
        // standalone: a tenant still serving a manifest without
        // `related_applications` also answers empty, and we'd wipe a true flag.
        if (installed || !isStandalone) remember(installed);
      })
      .catch(() => {
        /* not a secure context, or the manifest declares no related apps */
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isStandalone]);

  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      // Suppress Chrome's mini-infobar so we can present our own UI on a gesture.
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      remember(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<'accepted' | 'dismissed' | null> => {
    if (!deferred) return null;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    // The event can only be used once; drop it so the button hides afterward.
    setDeferred(null);
    if (choice.outcome === 'accepted') remember(true);
    return choice.outcome;
  };

  const isInstalled = isStandalone || wasInstalled;
  const offerInstall = !isInstalled && !checking;

  return {
    canInstall: !!deferred && offerInstall,
    isIOS: isIOS && offerInstall,
    isStandalone,
    isInstalled,
    promptInstall,
  };
}
